import { useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { AlertTriangle, Clock } from 'lucide-react';
import { PickupSlot } from '@/types';
import { getSlotIcon } from './slotIcons';
import { formatSlotWindow12 } from '@/utils/helpers';

interface SlotPickerProps {
  slots: PickupSlot[];
  value?: string;
  onChange: (code: string) => void;
  filterCodes?: string[];
  // Same-day-only variant (rollover "Change" flow): all 5 slots still render,
  // but the ones that have already rolled to tomorrow are disabled instead of
  // hidden, and the amber "tomorrow" note is suppressed on those rows.
  sameDayOnly?: boolean;
}

const SlotPicker = ({
  slots,
  value,
  onChange,
  filterCodes,
  sameDayOnly = false,
}: SlotPickerProps) => {
  const { t } = useTranslation();
  const visibleSlots = filterCodes?.length
    ? slots.filter((slot) => filterCodes.includes(slot.code))
    : slots;

  const isRowDisabled = (slot: PickupSlot) => sameDayOnly && slot.is_next_day;
  const enabledSlots = visibleSlots.filter((slot) => !isRowDisabled(slot));
  // Roving tabindex (WAI-ARIA radiogroup pattern): the selected row is the
  // only tabbable stop — but ONLY if it is itself enabled. In the same-day-only
  // flow the seeded `value` is the pickup's current slot, which is the one that
  // already rolled to tomorrow (disabled here); a disabled <button> is
  // unfocusable, so tabbing it would leave the whole group keyboard-unreachable.
  // Fall back to the first enabled row whenever `value` is missing or disabled.
  const rovingCode = enabledSlots.some((slot) => slot.code === value)
    ? value
    : enabledSlots[0]?.code;

  const rowRefs = useRef<Map<string, HTMLButtonElement>>(new Map());

  const registerRowRef = (code: string) => (el: HTMLButtonElement | null) => {
    if (el) {
      rowRefs.current.set(code, el);
    } else {
      rowRefs.current.delete(code);
    }
  };

  // Arrow/Home/End navigation moves selection among ENABLED rows only,
  // wrapping at the ends, and reuses the same onChange the click handler
  // calls — no separate "focused but unselected" state to track.
  const handleKeyDown = (event: React.KeyboardEvent<HTMLDivElement>) => {
    if (!enabledSlots.length) return;
    const currentIndex = enabledSlots.findIndex((slot) => slot.code === rovingCode);
    let nextIndex: number;

    switch (event.key) {
      case 'ArrowDown':
        nextIndex = currentIndex === -1 ? 0 : (currentIndex + 1) % enabledSlots.length;
        break;
      case 'ArrowUp':
        nextIndex =
          currentIndex === -1
            ? enabledSlots.length - 1
            : (currentIndex - 1 + enabledSlots.length) % enabledSlots.length;
        break;
      case 'Home':
        nextIndex = 0;
        break;
      case 'End':
        nextIndex = enabledSlots.length - 1;
        break;
      default:
        return;
    }

    event.preventDefault();
    const nextSlot = enabledSlots[nextIndex];
    if (!nextSlot) return;
    onChange(nextSlot.code);
    rowRefs.current.get(nextSlot.code)?.focus();
  };

  return (
    <div
      className='flex w-full flex-col gap-2'
      role='radiogroup'
      aria-label={String(t('select_pickup_slot_title'))}
      onKeyDown={handleKeyDown}
    >
      {visibleSlots.map((slot) => (
        <SlotPickerRow
          key={slot.code}
          slot={slot}
          active={value === slot.code}
          disabled={isRowDisabled(slot)}
          sameDayOnly={sameDayOnly}
          tabIndex={slot.code === rovingCode ? 0 : -1}
          onSelect={() => onChange(slot.code)}
          buttonRef={registerRowRef(slot.code)}
        />
      ))}
    </div>
  );
};

interface SlotPickerRowProps {
  slot: PickupSlot;
  active: boolean;
  disabled: boolean;
  sameDayOnly: boolean;
  tabIndex: number;
  onSelect: () => void;
  buttonRef: (el: HTMLButtonElement | null) => void;
}

const SlotPickerRow = ({
  slot,
  active,
  disabled,
  sameDayOnly,
  tabIndex,
  onSelect,
  buttonRef,
}: SlotPickerRowProps) => {
  const { t } = useTranslation();
  const SlotIcon = getSlotIcon(slot.code);
  const showTomorrowNote = slot.is_next_day && !sameDayOnly;

  // v2-task-8b (§1.1 fix #2 / §2.5): the selected state is carried by
  // border-WIDTH (1px → 2px), never by an outer box-shadow ring. A
  // box-shadow ring paints outside the element's border-box and gets
  // clipped by SlotModal's overflow-y-auto scroll container (the reported
  // "cropped border" bug, worst on the first/last row); a border
  // participates in normal box layout so it can never be clipped that way.
  // Border+padding sum is held constant (1px+12px = 2px+11px = 13px) so
  // toggling selection never shifts row height/width (no layout jank).
  // CRITICAL: this project's tailwind.config sets `important: true`, so a
  // `p-3` utility compiles to `padding:…!important` and would BEAT the
  // selected row's inline `padding:'11px'`, silently keeping it at 12px and
  // reintroducing the exact 1px shift this fix removes. So the padding
  // utility is applied ONLY to unselected rows; the selected row's 11px
  // comes purely from inline style with no competing class to override it.
  // Hover/focus/active feedback is added only for non-selected, enabled
  // rows — the selected tint already reads as its own state and shouldn't
  // also swap on hover.
  const rowClassName = [
    'flex w-full items-center gap-3 rounded-xl text-start transition-all',
    'active:scale-[0.98]',
    'focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-[rgb(237,224,74)]',
    'disabled:cursor-not-allowed disabled:opacity-45',
    !active ? 'p-3 bg-white' : '',
    !active && !disabled ? 'hover:bg-[rgb(245,245,243)]' : '',
  ]
    .filter(Boolean)
    .join(' ');

  return (
    <button
      ref={buttonRef}
      type='button'
      role='radio'
      aria-checked={active}
      disabled={disabled}
      tabIndex={tabIndex}
      onClick={onSelect}
      className={rowClassName}
      style={
        active
          ? {
              border: '2px solid rgb(237,224,74)',
              background: 'rgba(237,224,74,0.12)',
              padding: '11px',
            }
          : { border: '1px solid rgb(232,230,227)' }
      }
    >
      {/* Icon tile radius is deliberately proportional to size, not a
          uniform value: this 9x9 (h-9/w-9) tile pairs with rounded-lg
          (8px); CurrentSlotBanner's larger 12x12 tile pairs with
          rounded-xl (12px). Do not "fix" these to match — see spec §3.2. */}
      <span
        className='flex h-9 w-9 shrink-0 items-center justify-center rounded-lg'
        style={{ background: active ? 'rgb(237,224,74)' : 'rgb(245,245,243)' }}
      >
        <SlotIcon size={18} color={active ? 'rgb(57,57,60)' : 'rgb(115,123,140)'} />
      </span>
      <span className='min-w-0 flex-1 text-start'>
        <span className='flex items-center gap-2'>
          <span className='text-base font-extrabold tracking-tight'>
            <bdi>
              {formatSlotWindow12(slot.window_from, slot.window_to)}
            </bdi>
          </span>
          <span
            className='rounded-lg px-1.5 py-0.5 text-[10px] font-bold'
            style={{ background: 'rgb(245,245,243)', color: 'rgb(115,123,140)' }}
          >
            {slot.code}
          </span>
          {/* v2-task-10fe §4.2: server-flag-only chip — is_closing_soon is
              the SOLE source of truth (Baghdad + operational-day accurate on
              the backend), never re-derived from a client clock. Guarded
              against `disabled` too: a rolled-over/disabled row is already
              unselectable and must never also read as "closing soon". */}
          {slot.is_closing_soon && !disabled ? (
            <span className='inline-flex items-center gap-1 rounded-full bg-amber-50 px-1.5 py-0.5 text-[10px] font-bold text-amber-700'>
              <Clock size={12} /> {String(t('slot_closing_soon'))}
            </span>
          ) : null}
          {/* The "will be tomorrow" signal is a compact inline chip (matching
              the closing-soon chip's height/baseline) rather than a stacked
              full-width amber sentence under every row — when several rows are
              next-day at once, three identical amber lines read as noise; the
              chip conveys the same fact per row without the visual clutter. */}
          {showTomorrowNote ? (
            <span className='inline-flex items-center gap-1 rounded-full bg-amber-50 px-1.5 py-0.5 text-[10px] font-bold text-amber-700'>
              <AlertTriangle size={12} /> {String(t('slot_tomorrow_chip'))}
            </span>
          ) : null}
        </span>
        <span className='mt-0.5 block text-xs font-medium text-[rgb(115,123,140)]'>
          {slot.label}
        </span>
      </span>
      {/* v2-task-12fix (H1): `border-2` alone renders 0px/none with Preflight
          off (see SlotToggleRow) — `border-solid` restores the visible
          radio-dot ring on both selected and unselected rows. */}
      <span
        className='flex h-5 w-5 shrink-0 items-center justify-center rounded-full border-2 border-solid'
        style={{
          borderColor: active ? 'rgb(237,224,74)' : 'rgb(200,200,196)',
          background: active ? 'rgb(237,224,74)' : 'transparent',
        }}
      >
        {active ? (
          <span className='h-2 w-2 rounded-full' style={{ background: 'rgb(57,57,60)' }} />
        ) : null}
      </span>
    </button>
  );
};

export default SlotPicker;
